import { SearchOutlined } from "@ant-design/icons";
import { useNavigate, useParams } from "react-router-dom";
import { useDebounce } from "use-debounce";

import { Button, Input, Spin, Table, Tooltip, message } from "antd";
import { ColumnsType } from "antd/es/table";
import { FilterValue, SorterResult } from "antd/es/table/interface";
import dayjs from "dayjs";
import duration from "dayjs/plugin/duration";
import relativeTime from "dayjs/plugin/relativeTime";
import { FC, Key, ReactNode, useEffect, useState } from "react";

import DowntimeModal from "@/components/DowntimeModal";
import DownloanModal from "@/components/Pledge/DownloanModal";
import SummaryModal from "@/components/Pledge/SummaryModal";
import Breadcrumbs from "@/components/common/Breadcrumb";
import ErrorModal from "@/components/common/ErrorModal";
import { IMAGES, ROUTES_URL } from "@/constant";
import DownloanPledgesDetails from "@/pages/Pledge/DownloanPledgesDetails";
import styles from "@/pages/Pledge/Pledge.module.css";
import {
  getTenantDowntime,
  getTenantMasterData,
  getTenantMasterPledgeData,
} from "@/services/Auth/AuthApi";
import {
  createMYPledge,
  createPledgesIdFromConsulate,
  getPledgeList,
} from "@/services/Pledge/PledgeApis";
import { useAuthStore } from "@/stores/authStore";
import { useMasterStore } from "@/stores/useMasterStore";
import { usePledgesStore } from "@/stores/usePledgesStore";
import { TenantMasterDataInterface } from "@/types/authService";
import { MdmData, PledgeItemInterface, SelectedPledgeItem } from "@/types/pledgeService";
import {
  getPledgesBelowMinimumAmount,
  validateMinimumDownloanAmountAfterRenewalP035,
  validatePledgesMinimumDownloanAmountP034,
} from "@/utils";
import { checkBranchOperatingHours } from "@/utils/operatingHours";

dayjs.extend(duration);
dayjs.extend(relativeTime);

const WEB_DOWNTIME_REFERENCE_ID = "DT000";

const PledgePage: FC = () => {
  const navigate = useNavigate();
  const { setPledges, reset } = usePledgesStore();
  const { currency } = useAuthStore();
  const { id } = useParams();
  const userId = useAuthStore((state) => state?.customer?.id);
  // const setSelectedPledgeData = useRenewStore((state) => state?.setSelectedPledgeData);
  // const setDownloanAmount = useRenewStore((state) => state?.setDownloanAmount);
  // const resetRenewStore = useRenewStore((state) => state?.reset);
  // const update = useRenewStore((state) => state?.update);
  const { isMalaysia, isSingapore } = useAuthStore();
  const { exemptOperationsValidationMap, fetchExemptOperationsValidation } = useMasterStore();

  const isBranchOutsideOperatingHours = (record: PledgeItemInterface): boolean => {
    if (!record?.branch) return false;
    const branchId = record.branch.id;
    const s015Val = exemptOperationsValidationMap[branchId];
    const { isOutside, isValidationEnabled } = checkBranchOperatingHours(
      record.branch,
      s015Val,
      isMalaysia,
    );
    return isValidationEnabled && isOutside;
  };

  const [search, setSearch] = useState("");
  const [expandedRowKeys, setExpandedRowKeys] = useState<Key[]>([]);
  const [tableData, setTableData] = useState<PledgeItemInterface[]>();
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });

  const [downloadModal, setDownloadModal] = useState(false);
  const [errorModal, setErrorModal] = useState("");
  const [summeryModal, setSummeryModal] = useState(false);
  const [loader, setLoader] = useState(false);
  const [newPledgeCreateLoader, setNewPledgeCreateLoader] = useState(false);
  const [downloanAmountFromMDM, setdownloanAmountFromMDM] = useState<number>(0);
  const [selectedRows, setSelectedRows] = useState<SelectedPledgeItem[]>([]);
  const [isBulkDownloan, setIsBulkDownloan] = useState(false);
  const [mdmRenewDetails, setMDMRenewDetails] = useState<TenantMasterDataInterface | null>(null);
  const [selectedPledge, setSelectedPledge] = useState<PledgeItemInterface | null>(null);
  const [debouncedSearch] = useDebounce(search, 500);
  const [othersMdmDetails, setOthersMdmDetails] = useState<MdmData[]>([]);
  const [downtimeWindow, setDowntimeWindow] = useState<{
    startTime: string;
    endTime: string;
  } | null>(null);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const isSuccess = params.get("status") === "true";
    const isFailed = params.get("status") === "false";
    const errorMsg = params.get("message");

    if (isSuccess) {
      message.success("Payment successful");
      window.history.replaceState({}, "", location.pathname);
    }
    if (isFailed) {
      message.error(errorMsg || "Payment failed");
      window.history.replaceState({}, "", location.pathname);
    }
  }, [location, navigate]);

  const checkDowntimeAndRun = async (action: () => void | Promise<void>): Promise<void> => {
    try {
      const res = await getTenantDowntime();
      const webDowntime = res?.data?.find(
        (item: any) => item?.reference_id === WEB_DOWNTIME_REFERENCE_ID,
      );

      if (webDowntime?.is_active && webDowntime?.is_in_downtime) {
        setDowntimeWindow({
          startTime: webDowntime.downtime_start_time,
          endTime: webDowntime.downtime_end_time,
        });
        return;
      }
    } catch (error) {
      console.error("Failed to check downtime status", error);
    }

    await action();
  };

  useEffect(() => {
    reset();
    void fetchMDMData();
    void fetchMDMDataForPledgeRenewValidation();
  }, []);

  useEffect(() => {
    if (id) {
      setNewPledgeCreateLoader(true);
      const timer = setTimeout(() => {
        void createCdd(id);
      }, 10000);

      return () => clearTimeout(timer);
    }
  }, [id]);

  useEffect(() => {
    if (tableData?.length) {
      const uniqueBranchIds = Array.from(
        new Set(tableData.map((item) => item.branch?.id).filter(Boolean)),
      );
      uniqueBranchIds.forEach((branchId) => {
        if (exemptOperationsValidationMap[branchId] === undefined) {
          void fetchExemptOperationsValidation(branchId);
        }
      });
    }
  }, [tableData, exemptOperationsValidationMap]);

  const fetchMDMDataForPledgeRenewValidation = async (): Promise<void> => {
    try {
      const params = new URLSearchParams({
        active: "true",
        reference_ids: "P029",
      });
      const response = await getTenantMasterPledgeData(params.toString());
      setMDMRenewDetails(response?.data?.[0]);
    } catch (error) {
      console.error("Error fetching customer:", error);
    }
  };
  const createCdd = async (id: string) => {
    try {
      if (!isMalaysia) {
        const resp = await createPledgesIdFromConsulate(id);
        const mpsgResponse = await Promise.all(
          resp?.pledge_renewal_ids?.map((pledgeRenewalPaymentId: number) =>
            createMYPledge({
              pledge_renewal_payment_id: pledgeRenewalPaymentId,
            }),
          ),
        );
        const successResp = mpsgResponse.find((item) => !item?.error);
        if (successResp) {
          message.success(successResp?.message);
        }
      }
      void getPledgeListHelper(1, pagination.pageSize, {});
    } catch (error) {
      console.error("Error creating CDD form:", error);
    } finally {
      setNewPledgeCreateLoader(false);
      navigate(ROUTES_URL.PLEDGE);
    }
  };

  const getPledgeListHelper = async (
    page: number,
    pageSize: number,
    sortConfig?: any,
  ): Promise<void> => {
    try {
      const payload = {
        page,
        limit: pageSize,
        search_key: debouncedSearch.trim(),
        is_applied_promotion_interest: "true",
        selected_txn_type: "e_renew",
        pledge_status_search_key: "open",
        ...sortConfig,
      };

      setLoader(true);

      const response = await getPledgeList(payload);

      setTableData(response?.data || []);
      setPagination((prev) => ({
        ...prev,
        current: page,
        pageSize,
        total: response?.total || response?.data?.length || 0,
      }));
    } catch (error) {
      console.error(error);
    } finally {
      setLoader(false);
    }
  };

  useEffect(() => {
    void getPledgeListHelper(1, pagination.pageSize, {});
  }, [debouncedSearch]);

  const handleTableChange = (
    paginationConfig: any,
    filters: Record<string, FilterValue | null>,
    sorter: SorterResult<any> | SorterResult<any>[],
  ): void => {
    const singleSorter = Array.isArray(sorter) ? sorter[0] : sorter;

    const sortField = singleSorter.order ? singleSorter.field : undefined;
    const order =
      singleSorter.order === "ascend"
        ? "asc"
        : singleSorter.order === "descend"
          ? "desc"
          : undefined;

    setPagination({
      ...pagination,
      current: paginationConfig.current,
      pageSize: paginationConfig.pageSize,
    });

    void getPledgeListHelper(paginationConfig.current, paginationConfig.pageSize, {
      sort_by: sortField,
      sort_order: order,
    });
  };

  const fetchMDMData = async (): Promise<void> => {
    try {
      const params = new URLSearchParams({
        active: "true",
        reference_ids: "P036,P033,P034,P035",
      });
      const response = await getTenantMasterPledgeData(params.toString());
      setdownloanAmountFromMDM(response?.data[0]?.value || 0);
      const filterMdm = response?.data?.filter((item: any) => item?.reference_id !== "P036");
      setOthersMdmDetails(filterMdm);
    } catch (error) {
      console.error("Error fetching customer:", error);
    }
  };

  const getMaxDownloanAmount = (
    originalPledgeAmount: number,
    currentOfferedPledgeAmount: number,
  ): number => {
    const orig = originalPledgeAmount || currentOfferedPledgeAmount;
    const percentage = downloanAmountFromMDM / 100;
    const minRemaining = Number((percentage * orig).toFixed(2));
    return Math.max(0, Number((currentOfferedPledgeAmount - minRemaining).toFixed(2)));
  };

  const downloanOnSubmit = async (downloanInputData: string): Promise<void> => {
    const pledge = tableData?.find((i) => i.id === Number(expandedRowKeys[0])) ?? null;
    if (!pledge) {
      message.error("Please select a correct pledge");
      return;
    }
    const requestedAmount = Number(pledge.offered_pledge_amount);
    const originalPledgeAmount = Number(
      pledge.original_offered_pledge_amount || pledge.offered_pledge_amount,
    );
    const downloanAmount = Number(downloanInputData);
    if (!downloanInputData || Number.isNaN(downloanAmount)) {
      return;
    }
    const percentage = downloanAmountFromMDM / 100;
    const minRemaining = Number((percentage * originalPledgeAmount).toFixed(2));
    const maxDownloanAmount = Math.max(0, Number((requestedAmount - minRemaining).toFixed(2)));

    // P034
    const findMinimumDownloanReduction = othersMdmDetails?.find(
      (item) => item?.reference_id === "P034",
    );
    const minimumRemainingAmount = findMinimumDownloanReduction?.value ?? 0;
    const invalidTicketAlphaCodes = validatePledgesMinimumDownloanAmountP034(
      [
        {
          ...pledge,
          downloanAmount,
        },
      ],
      minimumRemainingAmount,
    );

    if (invalidTicketAlphaCodes.length) {
      setDownloadModal(false);

      setErrorModal(
        `Downloan Not Allowed. Customers must choose to at least pay ${currency} ${minimumRemainingAmount} towards their loan. Pledge(s): ${invalidTicketAlphaCodes.join(
          ", ",
        )}.`,
      );

      return;
    }
    // P035
    const findAfterRenewalReduction = othersMdmDetails?.find(
      (item) => item?.reference_id === "P035",
    );
    const minimumRenewalAmountForP035 = findAfterRenewalReduction?.value ?? 0;
    const invalidP035Pledges = validateMinimumDownloanAmountAfterRenewalP035(
      [
        {
          ...pledge,
          downloanAmount,
        },
      ],
      minimumRenewalAmountForP035,
    );
    if (invalidP035Pledges.length) {
      const errorMsg = `Downloan Not Allowed for pledge(s): ${invalidP035Pledges.join(", ")}. The minimum downloan amount is ${currency} ${minimumRenewalAmountForP035.toFixed(
        2,
      )}. Please enter a valid amount.`;
      message.error(errorMsg);
      return;
    }

    // Maximum downloan amount validation
    if (downloanAmount > maxDownloanAmount) {
      setDownloadModal(false);
      setErrorModal(
        `The requested downloan amount exceeds the maximum allowed amount of ${currency} ${maxDownloanAmount}. Please visit the store for assistance.`,
      );
      return;
    }
    setPledges([
      {
        ...pledge,
        downloanAmount,
      },
    ]);

    navigate(ROUTES_URL.CUSTOMER_CONCENT);
  };
  const startRenewProcessHelper = async (data?: PledgeItemInterface): Promise<void> => {
    if (!data?.id) {
      return;
    }

    // setSelectedPledgeData(data ?? null);
    // setDownloanAmount(0);

    // const { data: cdd } = await getCddList({
    //   pledge_id: data?.id,
    // });
    // const cdd_info = cdd?.[0]?.cdd_info;
    // const cdd_type = cdd?.[0]?.cdd_type;

    // update({
    //   cddInfo: cdd_info,
    //   cddType: cdd_type,
    // });
    setPledges([{ ...data, downloanAmount: 0 }]);
    navigate(ROUTES_URL.CUSTOMER_CONCENT);
  };

  const viewTicketHelper = (data?: PledgeItemInterface): void => {
    if (!data?.id) {
      return;
    }

    // setSelectedPledgeData(data ?? null);
    // setDownloanAmount(0);

    setPledges([{ ...data, downloanAmount: 0 }]);
    navigate(ROUTES_URL.VIEW_PLEDGE_TICKET);
  };

  const withTooltip = (text: string): ReactNode => (
    <div className={styles.pledgeCell}>
      <span className={styles.pledgeText}>{text}</span>
      {text ? (
        <Tooltip title={text} placement="topRight" color="white">
          <img src={IMAGES.RED_INFO_ICON} alt="info" />
        </Tooltip>
      ) : (
        "-"
      )}
    </div>
  );

  const columns: ColumnsType<PledgeItemInterface> = [
    {
      title: "Ticket Number",
      dataIndex: "ticket_alpha_code",
      key: "ticket_alpha_code",
      render: (value: string) => <span>{value}</span>,
    },
    {
      title: "Ticket Date",
      dataIndex: "pledge_date",
      key: "pledge_date",
      render: (_, record) => (
        <div> {dayjs(record?.pledge_date).format("DD MMM YYYY, hh:mm A")}</div>
      ),
    },
    {
      title: "Branch Name",
      key: "branch",
      render: (_, record) => (
        <div>
          <div>
            {record.branch?.branch_name}
            <span className="ml-1">({record.branch?.branch_code})</span>
          </div>
        </div>
      ),
    },
    {
      title: "Expiry Date",
      dataIndex: "pledge_expiry_date",
      key: "pledge_expiry_date",
      render: (_, record) => (
        <div> {dayjs(record?.pledge_expiry_date).format("DD MMM YYYY, hh:mm A")}</div>
      ),
    },
    {
      title: `Pledge Amount (${currency})`,
      dataIndex: "offered_pledge_amount",
      key: "offered_pledge_amount",
      render: (value: number | string) => <span>{value}</span>,
    },
    {
      title: `Interest (${currency})`,
      dataIndex: "total_interest_amount",
      key: "total_interest_amount",
      render: (value: number | string) => <span>{value}</span>,
    },
    {
      title: "Remarks",
      dataIndex: "remarks",
      key: "remarks",
      render: withTooltip,
    },
  ];
  const getBulkDownloanPledges = (prev: SelectedPledgeItem[]) => {
    return prev.map((item) => ({
      ...item,
      downloanAmount: getMaxDownloanAmount(
        item?.original_offered_pledge_amount,
        item?.offered_pledge_amount,
      ),
    }));
  };

  const onDownloanClick = (): void => {
    const invalidPledgeTickets = getPledgesBelowMinimumAmount(
      selectedRows ?? [],
      othersMdmDetails ?? [],
    );

    if (invalidPledgeTickets.length) {
      message.error(
        `Downloan Not Allowed for pledge(s): ${invalidPledgeTickets.join(", ")}. The pledge amount must be greater than the minimum allowed amount.`,
      );
      return;
    }
    setSelectedRows(getBulkDownloanPledges);
    setIsBulkDownloan(true);
  };
  const onBulkRenewClick = (): void => {
    try {
      const updatedPledges = selectedRows?.map((item) => ({ ...item, downloanAmount: 0 }));
      setPledges(updatedPledges);
      navigate(ROUTES_URL.CUSTOMER_CONCENT);
    } catch (error) {
      console.error("error", error);
    }
  };
  const isPledgeSelectionDisabled = (record: PledgeItemInterface): boolean => {
    const pledgeDate = dayjs(record?.pledge_date);
    const daysDifference = dayjs().diff(pledgeDate, "day");
    const isWithinLockPeriod = daysDifference < (mdmRenewDetails?.value ?? 0);
    return isWithinLockPeriod;
  };

  const mapSelectedRows = (rows: PledgeItemInterface[], prev: SelectedPledgeItem[]) => {
    return rows.map((item) => {
      const existingItem = prev.find((prevItem) => prevItem.id === item.id);
      return {
        ...item,
        downloanAmount:
          existingItem?.downloanAmount ||
          getMaxDownloanAmount(item?.original_offered_pledge_amount, item?.offered_pledge_amount),
      };
    });
  };

  const onRowSelect = (rows: PledgeItemInterface[]): void => {
    setSelectedRows((prev) => mapSelectedRows(rows, prev));
  };

  const calculateActiveMonths = (startDateStr: string): string => {
    const start = new Date(startDateStr);
    const end = new Date();
    let months =
      (end.getFullYear() - start.getFullYear()) * 12 + (end.getMonth() - start.getMonth());
    if (end.getDate() < start.getDate()) {
      months -= 1;
    }

    const activeMonths = months + 1;
    return `${activeMonths} ${activeMonths === 1 ? "Month" : "Months"}`;
  };

  return (
    <div className={styles.container}>
      <Spin
        spinning={newPledgeCreateLoader}
        fullscreen
        tip="Payment in progress. Please do not close this window."
      />
      <div className={styles.customBreadCrumbs}>
        <Breadcrumbs items={[{ label: "Pledges" }]} />
      </div>
      <div className={styles.mainContainer}>
        <div className={styles.tenantRightActions}>
          <div className="flex justify-between gap-3">
            <Button
              className={styles.applyButton}
              disabled={!selectedRows?.length}
              htmlType="submit"
              onClick={() => checkDowntimeAndRun(onDownloanClick)}
            >
              Bulk Downloan
            </Button>
            <Button
              className={styles.applyButton}
              disabled={!selectedRows?.length}
              htmlType="submit"
              onClick={() => checkDowntimeAndRun(onBulkRenewClick)}
            >
              Bulk Renew
            </Button>
          </div>
          <Input
            placeholder="Search pledges..."
            suffix={<SearchOutlined className={styles.searchInputIcon} />}
            className={styles.searchInput}
            allowClear
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <div className={styles.tableWrapper}>
          <Table
            columns={columns}
            dataSource={tableData}
            rowKey="id"
            loading={loader}
            pagination={{
              ...pagination,
              size: "small",
            }}
            rowSelection={{
              onChange: (_, rows) => {
                onRowSelect(rows);
              },
              getCheckboxProps: (record) => ({
                disabled:
                  isPledgeSelectionDisabled(record) ||
                  isBranchOutsideOperatingHours(record) ||
                  record?.total_interest_amount <= 0,
              }),
            }}
            rowClassName={(_, index) => (index % 2 === 0 ? styles.evenRow : styles.oddRow)}
            onChange={handleTableChange}
            expandable={{
              expandedRowRender: (pledgeData) => (
                <div className={styles.expandableBG}>
                  <div className={styles.totalItems}>
                    <div className={styles.months}>
                      <img
                        src={IMAGES.CALENDAR_ICON}
                        className={styles.expandIcon}
                        alt="calender-icon"
                      />
                      <span className={styles.labelTitle}>Duration</span>
                    </div>
                    <span className={styles.values}>
                      {calculateActiveMonths(pledgeData?.pledge_date)}
                    </span>
                    <div className={styles.button}>
                      <div className={styles.buttonGroup}>
                        <div className={styles.firstGroupButton}>
                          <Button
                            className={styles.clearButton}
                            disabled={
                              isPledgeSelectionDisabled(pledgeData) ||
                              isBranchOutsideOperatingHours(pledgeData) ||
                              getPledgesBelowMinimumAmount(
                                pledgeData ? [pledgeData] : [],
                                othersMdmDetails ?? [],
                              ).length > 0
                            }
                            onClick={() =>
                              checkDowntimeAndRun(() => {
                                setSelectedPledge(pledgeData);
                                setDownloadModal(true);
                              })
                            }
                          >
                            Downloan
                          </Button>
                          <Button
                            className={styles.applyButton}
                            htmlType="submit"
                            disabled={
                              isPledgeSelectionDisabled(pledgeData) ||
                              isBranchOutsideOperatingHours(pledgeData)
                            }
                            onClick={() =>
                              checkDowntimeAndRun(() =>
                                startRenewProcessHelper(
                                  tableData?.find((i) => i.id === Number(expandedRowKeys[0])) ??
                                    undefined,
                                ),
                              )
                            }
                          >
                            Renew
                          </Button>
                        </div>
                        <div className={styles.lastGroupButton}>
                          <Button
                            className={styles.viewTicket}
                            onClick={() =>
                              viewTicketHelper(
                                tableData?.find((i) => i.id === Number(expandedRowKeys[0])) ??
                                  undefined,
                              )
                            }
                          >
                            View Ticket
                          </Button>
                          {/* <span>This is pledge not eligible to renew now.</span> */}
                        </div>
                      </div>
                    </div>

                    <div className={styles.fullLabel}>
                      <img
                        src={IMAGES.BRANCH_ICON}
                        className={styles.expandIcon}
                        alt="branch-icon"
                      />
                      <span className={styles.labelTitle}>Branch</span>
                    </div>
                    <span className={styles.values}>
                      {
                        tableData?.find((i) => i.id === Number(expandedRowKeys[0]))?.branch
                          ?.branch_name
                      }
                    </span>

                    <div className={styles.fullLabel}>
                      <img
                        src={IMAGES.TICKET_ICON}
                        className={styles.expandIcon}
                        alt="ticket-icon"
                      />
                      <span className={styles.labelTitle}>{`Ticket Amount (${currency})`}</span>
                    </div>
                    <span className={styles.values}>
                      {
                        tableData?.find((i) => i.id === Number(expandedRowKeys[0]))
                          ?.offered_pledge_amount
                      }
                    </span>

                    {/* <div className={styles.fullLabel}>
                      <img src={IMAGES.ARTICLE_ICON} className={styles.expandIcon} />
                      <span className={styles.labelTitle}>Article Description</span>
                    </div>
                    <span className={styles.values}>1/CHARCOLE</span> */}
                  </div>
                  {isPledgeSelectionDisabled(pledgeData) && (
                    <div className={styles.warningContainer}>
                      <img
                        src={IMAGES.APPROVAL_DANGER_ICON}
                        className={styles.deleteIcon}
                        alt="approval-danger"
                      />
                      <span className={styles.warningText}>
                        {`Renewal is allowed only after ${mdmRenewDetails?.value} days from the pledge creation date.`}
                      </span>
                    </div>
                  )}
                  {isBranchOutsideOperatingHours(pledgeData) && (
                    <div className={styles.warningContainer}>
                      <img
                        src={IMAGES.APPROVAL_DANGER_ICON}
                        className={styles.deleteIcon}
                        alt="approval-danger"
                      />
                      <span className={styles.warningText}>
                        {checkBranchOperatingHours(
                          pledgeData?.branch,
                          exemptOperationsValidationMap[pledgeData?.branch?.id],
                          isMalaysia,
                        )?.message || "Transactions are blocked outside branch operating hours."}
                      </span>
                    </div>
                  )}
                  {getPledgesBelowMinimumAmount(
                    pledgeData ? [pledgeData] : [],
                    othersMdmDetails ?? [],
                  )?.length > 0 && (
                    <div className={styles.warningContainer}>
                      <img
                        src={IMAGES.APPROVAL_DANGER_ICON}
                        className={styles.deleteIcon}
                        alt="approval-danger"
                      />
                      <span className={styles.warningText}>
                        {`Downloan Not Allowed for pledge. The pledge amount must be greater than the minimum allowed amount.`}
                      </span>
                    </div>
                  )}
                  {pledgeData?.is_eligible_for_uploan && (
                    <div className={styles.warningCard}>
                      <div className={styles.downloanImageBox}>
                        <img
                          src={IMAGES.DOWNLOAD_ICON}
                          className={styles.downloanIcon}
                          alt="download"
                        />
                      </div>
                      <span className={styles.warningText}>
                        You're eligible for an uploan. Please visit to the retail pawnshop outlet to
                        avail it at earliest possible.
                      </span>
                    </div>
                  )}
                </div>
              ),
              expandIconColumnIndex: columns.length + 1,
              expandIcon: ({ expanded, onExpand, record }) =>
                expanded ? (
                  <button
                    type="button"
                    onClick={(e) => onExpand(record, e)}
                    style={{ background: "none", border: "none", padding: 0, cursor: "pointer" }}
                  >
                    <img src={IMAGES.UP_ARROW} alt="up arrow" />
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={(e) => onExpand(record, e)}
                    style={{ background: "none", border: "none", padding: 0, cursor: "pointer" }}
                  >
                    <img src={IMAGES.DOWN_ARROW} alt="up arrow" />
                  </button>
                ),
              expandedRowKeys,
              onExpand: (expanded, record) => {
                setExpandedRowKeys(expanded ? [record.id] : []);
              },
            }}
          />
        </div>
      </div>
      {errorModal ? (
        <ErrorModal
          open={!!errorModal}
          title="Downloan Not Allowed"
          subTitle={errorModal}
          onClose={() => setErrorModal("")}
        />
      ) : null}
      {downloadModal ? (
        <DownloanModal
          open={downloadModal}
          onClose={() => setDownloadModal(false)}
          onSubmit={downloanOnSubmit}
          initialDownloanAmount={getMaxDownloanAmount(
            selectedPledge?.original_offered_pledge_amount ?? 0,
            selectedPledge?.offered_pledge_amount
              ? Number(selectedPledge?.offered_pledge_amount)
              : 0,
          )}
        />
      ) : null}
      {summeryModal ? (
        <SummaryModal open={summeryModal} onClose={() => setSummeryModal(false)} />
      ) : null}
      <DownloanPledgesDetails
        open={isBulkDownloan}
        onClose={() => setIsBulkDownloan(false)}
        pledgesDetails={selectedRows}
        setSelectedRows={setSelectedRows}
        downloanAmountFromMDM={downloanAmountFromMDM}
        othersMdmDetails={othersMdmDetails}
      />

      {downtimeWindow ? (
        <DowntimeModal
          open={Boolean(downtimeWindow)}
          startTime={downtimeWindow.startTime}
          endTime={downtimeWindow.endTime}
          onClose={() => setDowntimeWindow(null)}
        />
      ) : null}
    </div>
  );
};
export default PledgePage;
